import React, { Suspense, useCallback } from 'react';
import { env } from '@tasama/config/portal';
import type { CtaItem } from '@tasama/ui';
import { CompanyType, ErrorBoundary } from '@tasama/ui';
import { isSafeUrl } from '@tasama/utils';
import { useIsAuthenticated } from '../../stores/auth-store';
import { useUserContext } from '../../stores/user-context.store';
import {
  useSupportTicketsStore,
  useNewTicketDrawerStore,
} from '../support-ticketing/store';
import { router } from '../../core/router/router';

/**
 * Lazy-loaded Business Advisor for the portal app.
 *
 * Uses router.navigate() directly so this component
 * can live anywhere in the tree — no Router context required.
 */
const BusinessAdvisorContainer = React.lazy(() =>
  import('@tasama/business-advisor').then((m) => ({
    default: m.BusinessAdvisorContainer,
  }))
);

export function BusinessAdvisorFeature() {
  const isAuthenticated = useIsAuthenticated();
  const userContext = useUserContext();
  const companyType = userContext?.companyType as CompanyType | undefined;
  const setPrefilledTicketData = useSupportTicketsStore(
    (s) => s.setPrefilledTicketData
  );
  const openTicketDrawer = useNewTicketDrawerStore((s) => s.open);

  const handleCtaClick = useCallback(
    (item: CtaItem) => {
      if (item.type === 'SUPPORT_TICKET') {
        const meta = item.cta_metadata;

        if (meta?.subject || meta?.description) {
          setPrefilledTicketData(meta.subject ?? '', meta.description ?? '');
        } else if (meta?.service_l3) {
          setPrefilledTicketData(meta.service_l3);
        }

        // Open new ticket drawer in-place — drawer is a global singleton in App.tsx
        openTicketDrawer(
          undefined,
          undefined,
          meta?.function_l1_sys_id ?? undefined,
          meta?.type_l2_sys_id ?? undefined,
          meta?.service_l3_sys_id ?? undefined,
          undefined,
          undefined,
          undefined,
          meta?.sr_ritm_sys_id ?? undefined
        );
      } else if (item.url && isSafeUrl(item.url)) {
        if (item.url.startsWith('/')) {
          router.navigate(item.url);
        } else {
          window.location.href = item.url;
        }
      }
    },
    [setPrefilledTicketData, openTicketDrawer]
  );

  return (
    <ErrorBoundary
      testID="business-advisor-feature-error-boundary"
      fallback={() => null}
    >
      <Suspense fallback={null}>
        <BusinessAdvisorContainer
          isAuthenticated={isAuthenticated}
          onCtaClick={handleCtaClick}
          onNavigate={(url) => router.navigate(url)}
          onboardingUrl={env.ONBOARDING_APP_BASE_URL}
          {...(companyType && { companyType })}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

export default BusinessAdvisorFeature;
